import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { Tool, ToolType } from '@/types'
import { getTool, updateTool } from '@/lib/api'
import { Card } from '@/components/ui/Card'
import { TextField, TextAreaField, SelectField, SaveBar } from '@/components/ui/FormFields'
import { PendingBackendNotice } from '@/components/ui/EmptyState'

export function ToolDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [tool, setTool] = useState<Tool | null>(null)
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    getTool(id).then(setTool)
  }, [id])

  if (!tool) return <div style={{ color: 'var(--text-secondary)' }}>Cargando…</div>

  async function handleSave() {
    if (!tool) return
    setSaving(true)
    setResult(null)
    try {
      await updateTool(tool.id, tool)
      setResult('Guardado — solo en esta sesión (sin backend conectado)')
    } catch (e) {
      setResult(e instanceof Error ? e.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  function update<K extends keyof Tool>(key: K, value: Tool[K]) {
    setTool((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  return (
    <div>
      <PendingBackendNotice endpoint={`PATCH /tools/${tool.id}`} />
      <button onClick={() => navigate('/tools')} style={backLinkStyle}>← Volver a Tools</button>
      <h1 style={{ fontSize: 20, fontWeight: 500, marginBottom: 16 }}>{tool.name}</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card>
          <TextField label="ID" value={tool.id} onChange={() => {}} mono />
          <TextField label="Nombre" value={tool.name} onChange={(v) => update('name', v)} />
          <TextAreaField label="Descripción" value={tool.description ?? ''} onChange={(v) => update('description', v)} />
          <TextAreaField label="Limitaciones" value={tool.limitations ?? ''} onChange={(v) => update('limitations', v)} rows={2} />
        </Card>
        <Card>
          <SelectField
            label="Tipo"
            value={tool.tool_type}
            onChange={(v) => update('tool_type', v as ToolType)}
            options={['acción', 'integración']}
          />
          <TextField label="Categoría" value={tool.category ?? ''} onChange={(v) => update('category', v)} />
          <TextField label="Provider" value={tool.provider ?? ''} onChange={(v) => update('provider', v)} />
          <TextField label="Vive en" value={tool.lives_on ?? ''} onChange={(v) => update('lives_on', v)} mono />
          <TextField label="Auth method" value={tool.auth_method ?? ''} onChange={(v) => update('auth_method', v)} />
          <TextField
            label="Scopes disponibles (separados por coma)"
            value={(tool.scopes_available ?? []).join(', ')}
            onChange={(v) => update('scopes_available', v.split(',').map((s) => s.trim()).filter(Boolean))}
            mono
          />
        </Card>
      </div>

      <SaveBar onSave={handleSave} saving={saving} result={result} />
    </div>
  )
}

const backLinkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--text-secondary)',
  fontSize: 13,
  cursor: 'pointer',
  padding: 0,
  marginBottom: 12,
  display: 'block',
}
